/**
 * Perfora GA4 Custom Events  — v3.0 (17-Sep-2026 audit)
 * Depends on ga4-datalayer.js being loaded first (window.PerforaGA4).
 *
 * ALL 23 CUSTOM EVENTS IMPLEMENTED:
 *  logo_click, navigation_interaction, hamburger_menu_interaction,
 *  category_selection, category_tab_click, cta_click, footer_interaction,
 *  email_signup_subscription, social_media_interaction,
 *  filter_applied, filter_removed, sort_applied, select_collection_category,
 *  faq_click, pack_size_select, dropdown_interaction,
 *  view_clinical_reports_click, search_interaction, scroll_depth,
 *  pincode_interaction, video_interaction, brand_logo_click,
 *  product_image_swipe, horizontal_scroll
 *
 * Data-attribute hooks — add these to your Liquid templates:
 *
 *  Logo:           data-ga4-logo                 on the header logo <a>
 *
 *  Desktop Nav:    data-ga4-nav-section="Shop"   on the <ul>/<div> wrapping a nav group
 *                  data-ga4-nav-link              on each child <a>
 *
 *  Hamburger:      data-ga4-hamburger-section="SHOP ALL"  on each drawer section wrapper
 *                  data-ga4-hamburger-link                 on each child <a>
 *
 *  CTA buttons:    data-ga4-cta                  on any <a> or <button>
 *                  data-ga4-cta-section="OUR STORY"  on the <a>/<button> or ancestor
 *
 *  Footer:         data-ga4-footer-section="QUICK LINKS"  on each column wrapper
 *                  data-ga4-footer-link                    on each <a>
 *
 *  Social icons:   data-ga4-social-name="Facebook"        on each social <a>
 *
 *  Category links: data-ga4-category-link="Electric Toothbrush"  on each Explore Oral Care <a>
 *
 *  Category tabs:  data-ga4-tab="Yellow Teeth"            on each tab button
 *                  data-ga4-tab-section="SHOP BY CONCERN" on the tab container
 *
 *  Email form:     data-ga4-email-form                    on the <form>
 *                  data-ga4-email-section="JOIN THE PERFORA FAMILY"  on form or ancestor
 *
 *  FAQ:            data-ga4-faq                           on each <details> (FAQ only, NOT PDP dropdowns)
 *
 *  PDP Dropdown:   data-ga4-dropdown                      on each <details> on PDP
 *
 *  Pack size:      data-ga4-pack-size="Pack of 2"         on each pack button/label
 *                  data-ga4-pack-product="Purple Serum"   on the button or ancestor
 *                  data-ga4-pack-section="SHOP BY CONCERN" on the section wrapper (NA on PDP)
 *
 *  Clinical:       data-ga4-clinical                      on the CTA
 *                  data-ga4-clinical-product="..."        on or near the CTA
 *
 *  Filter form:    data-ga4-filter-form                   on the <form>
 *                  data-ga4-collection-name="Electric Toothbrush"  on the form
 *
 *  Sort select:    data-ga4-sort-select                   on the <select>
 *
 *  Collection cat: data-ga4-collection-cat                on each category pill <button>/<a>
 *                  data-ga4-collection-name="..."         on the container
 *
 *  Search form:    data-ga4-search-form                   on the <form>
 *                  data-ga4-search-result="product name"  on each suggestion <a>
 *                  data-ga4-search-section="POPULAR CHOICES"  on the suggestion container
 *
 *  Video:          data-ga4-video                         on each <video>, <button[play]>, etc.
 *                  data-ga4-video-product="..."           on or near the video
 *
 *  Brand logos:    data-ga4-brand-logo                    on each media/press logo <a>
 *                  data-ga4-brand-name="Myntra"           on the <a>
 *                  data-ga4-brand-section="AS SEEN IN"    on the section wrapper or <a>
 *
 *  Product images: data-ga4-image-gallery                 on the gallery container
 *                  data-ga4-gallery-product="..."         on the gallery container
 *
 *  Horiz scroll:   data-ga4-hscroll                       on each horizontally-scrollable section
 *                  data-ga4-hscroll-name="Explore Oral Care"  on the scrollable container
 *                  (Homepage only — do NOT add on Product Page)
 *
 *  Pincode:        data-ga4-pincode-form                  on the pincode check form/button
 *                  data-ga4-pincode-product="..."         on or near it
 */

(function () {
  'use strict';

  if (window.__perforaGA4CustomEventsLoaded) return;
  window.__perforaGA4CustomEventsLoaded = true;

  /* ── Helpers ─────────────────────────────────────────────── */
  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else { fn(); }
  }

  function platform() {
    return window.matchMedia('(max-width: 768px)').matches ? 'm-web' : 'd-web';
  }

  function pageType() {
    var path = (window.location.pathname || '').toLowerCase();
    var query = (window.location.search || '').toLowerCase();
    if (path.indexOf('/search') !== -1 || path.indexOf('searchtap') !== -1 || (path.indexOf('/pages/') !== -1 && path.indexOf('search') !== -1) || query.indexOf('q=') !== -1) {
      return 'search_page';
    }
    return (window.PerforaGA4 && window.PerforaGA4.page_type) || 'other_page';
  }

  // AUDIT FIX: user_id → user_id_event; guest = undefined (not null)
  function userId() {
    var cfg = window.PerforaGA4 || {};
    var isLogged = (cfg.user_status === 'logged_in' || cfg.user_type === 'logged_in');
    return (isLogged && cfg.user_id_event != null)
      ? String(cfg.user_id_event)
      : undefined;
  }

  function userType() {
    var cfg = window.PerforaGA4 || {};
    return cfg.user_type || cfg.user_status || 'guest';
  }

  function base() {
    var b = {
      page_type: pageType(),
      platform: platform(),
      user_type: userType()
    };
    var uid = userId();
    b.user_id_event = uid !== undefined ? uid : undefined;
    return b;
  }

  function push(eventName, extra) {
    window.dataLayer = window.dataLayer || [];
    var payload = Object.assign({ event: eventName }, base(), extra || {});
    window.dataLayer.push(payload);
    if (window.PerforaGA4 && window.PerforaGA4.debug) {
      console.log('[GA4-Custom]', eventName, payload);
    }
  }

  function closest(el, selector) {
    if (!el) return null;
    if (el.closest) return el.closest(selector);
    var cur = el;
    while (cur) {
      try { if (cur.matches && cur.matches(selector)) return cur; } catch (e) { }
      cur = cur.parentElement;
    }
    return null;
  }

  function attr(el, name, fallback) {
    return (el && el.getAttribute && el.getAttribute(name)) || fallback || 'NA';
  }

  function text(el) {
    if (!el) return 'NA';
    var t = (el.textContent || el.innerText || '').replace(/\s+/g, ' ').trim();
    return t || 'NA';
  }

  /* ═══════════════════════════════════════════════════════════
     1. logo_click (Handled in theme.liquid to support broader selectors)
  ════════════════════════════════════════════════════════════ */

  /* ═══════════════════════════════════════════════════════════
     2. navigation_interaction  (Desktop nav only)
     AUDIT FIX: section_name must be the PARENT group, not cta_text.
     AUDIT FIX: must NOT also fire faq_click.
  ════════════════════════════════════════════════════════════ */
  function bindNavigation() {
    document.addEventListener('click', function (e) {
      // Guard: don't fire if inside mobile drawer (hamburger menu)
      if (e.target.closest('.js-menu-drawer')) return;

      // Only fire inside desktop nav
      var nav = e.target.closest('.header__nav');
      if (!nav) return;

      // Look for a link OR a top-level summary
      var lnk = e.target.closest('a, summary.menu__link');
      if (!lnk) return;

      // Find the top-level menu item
      var topItem = lnk.closest('.menu__item');
      var sectionName = 'Main Menu';

      if (topItem) {
        var topLevelLink = topItem.querySelector('summary, a');
        if (topLevelLink) {
          sectionName = (topLevelLink.getAttribute('aria-label') || topLevelLink.innerText || topLevelLink.textContent || '').split('\n')[0].trim();
        }
      }

      // If they clicked the top-level item itself, sectionName is "Main Menu"
      var isTopLevel = topItem && (lnk === topItem.querySelector('summary, a'));
      if (isTopLevel) {
        sectionName = 'Main Menu';
      }

      var linkText = (lnk.getAttribute('aria-label') || lnk.innerText || lnk.textContent || '').split('\n')[0].trim() || 'NA';

      push('navigation_interaction', {
        section_name: sectionName,
        cta_text: linkText,
        destination_url: lnk.tagName.toLowerCase() === 'summary' ? 'NA' : (lnk.href || 'NA')
      });
    }, true);
  }


  /* ═══════════════════════════════════════════════════════════
     3. hamburger_menu_interaction  (Mobile drawer)
     AUDIT FIX: section_name must be the PARENT group, not cta_text.
     AUDIT FIX: must NOT fire cta_click when clicking hamburger icon or cart.
  ════════════════════════════════════════════════════════════ */
  function bindHamburger() {
    document.addEventListener('click', function (e) {
      var drawer = closest(e.target, '.js-menu-drawer, #menuDrawer, ecx-menu-drwer');
      if (!drawer) return;

      var link = closest(e.target, 'a');
      if (!link) return;

      var cta = text(link);
      // Clean badge text from CTA if present (e.g. "Yellow TeethRecommended" -> "Yellow Teeth")
      if (link.querySelector('span:nth-child(2)')) {
        var clone = link.cloneNode(true);
        var badge = clone.querySelector('span:nth-child(2)');
        if (badge && /recommended/i.test(badge.textContent)) {
          badge.remove();
          cta = text(clone);
        }
      }

      var sectionEl = closest(e.target, '[data-ga4-hamburger-section], [data-ga4-nav-section]');
      var sectionName = sectionEl ? (sectionEl.getAttribute('data-ga4-hamburger-section') || sectionEl.getAttribute('data-ga4-nav-section')) : '';

      // Fallback: check if inside mobile-mega-menu-content container
      if (!sectionName) {
        var mobileBlock = closest(link, '.mobile-mega-menu-content, [class*="mobile-mega-menu"]');
        if (mobileBlock) {
          var headingEl = mobileBlock.querySelector('.main_menu, h2, h3, p');
          if (headingEl) {
            sectionName = text(headingEl);
          }
        }
      }

      // Fallback: check if inside user-account-drawer
      if (!sectionName && closest(link, '.user-account-drawer')) {
        sectionName = 'User Account';
      }

      // Fallback: check if inside contact wrapper
      if (!sectionName && closest(link, '.header__contact, .contact__wrapper, .header-extra-info')) {
        sectionName = 'Contact Us';
      }

      var ctaLower = cta.toLowerCase().trim();
      var hrefLower = (link.getAttribute('href') || '').toLowerCase();

      var shopAllKeywords = [
        'bestseller', 'electric toothbrush', 'toothpaste', 'mouthwash',
        'flosser', 'teeth whitening', 'tongue cleaner', 'mouth freshener',
        'kids range', 'value pack', 'all products'
      ];
      var oralConcernsKeywords = [
        'yellow teeth', 'bad breath', 'plaque', 'sensitive teeth', 'sensitivity'
      ];

      var sLower = (sectionName || '').toLowerCase().trim();
      if (sLower === 'shop all' || sLower === 'shop-all') {
        sectionName = 'SHOP ALL';
      } else if (sLower === 'oral concerns' || sLower === 'oral-concerns') {
        sectionName = 'ORAL CONCERNS';
      } else if (!sectionName || sLower === 'main menu' || sLower === 'na') {
        var isShopAll = shopAllKeywords.some(function (k) { return ctaLower.indexOf(k) !== -1; }) ||
          hrefLower.indexOf('/electric-toothbrush') !== -1 ||
          hrefLower.indexOf('/bestsellers') !== -1 ||
          hrefLower.indexOf('/toothpaste-mouthwash') !== -1 ||
          hrefLower.indexOf('/dental-flossers') !== -1 ||
          hrefLower.indexOf('/mouth-freshners') !== -1 ||
          hrefLower.indexOf('/kids-range') !== -1 ||
          hrefLower.indexOf('/value-pack') !== -1 ||
          hrefLower.indexOf('/collections/all') !== -1;

        var isOralConcerns = oralConcernsKeywords.some(function (k) { return ctaLower.indexOf(k) !== -1; }) ||
          hrefLower.indexOf('/fresh-breath') !== -1 ||
          hrefLower.indexOf('/plaque-removal') !== -1 ||
          hrefLower.indexOf('/sensitivity') !== -1;

        if (isShopAll) {
          sectionName = 'SHOP ALL';
        } else if (isOralConcerns) {
          sectionName = 'ORAL CONCERNS';
        } else {
          sectionName = sectionName || 'Main Menu';
        }
      }

      push('hamburger_menu_interaction', {
        section_name: sectionName,
        cta_text: cta,
        destination_url: link.href || 'NA'
      });
    }, true);
  }

  /* ═══════════════════════════════════════════════════════════
     4. category_selection  (Explore Oral Care — Homepage)
  ════════════════════════════════════════════════════════════ */
  function bindCategorySelection() {
    var lastCategoryClickTime = 0;

    document.addEventListener('click', function (e) {
      // Ignore carousel navigation arrow buttons and pagination
      if (closest(e.target, '.js-carousel-prev-btn, .js-carousel-next-btn, .carousel-pagination, .s-footer')) {
        return;
      }

      // 1. Direct attribute match
      var target = closest(e.target, '[data-ga4-category-link]');
      var sectionContainer = null;
      var cardItem = null;

      // 2. If no direct data-ga4-category-link, check if clicked inside Explore Oral Care section
      if (!target) {
        sectionContainer = closest(e.target, [
          '[data-ga4-category-section]',
          '.image-with-text-pro',
          '[data-ui-component="Image With Text Columns Section"]',
          '.image-with-text-columns'
        ].join(','));

        if (!sectionContainer) {
          var potentialSec = closest(e.target, '.shopify-section, section, div.section');
          if (potentialSec) {
            var headingEl = potentialSec.querySelector('.s-header__title, h2, h1');
            if (headingEl && /explore/i.test(headingEl.textContent) && /oral care/i.test(headingEl.textContent)) {
              sectionContainer = potentialSec;
            }
          }
        }

        if (sectionContainer) {
          cardItem = closest(e.target, 'ecx-carousel-itm, .js-carousel-item, .carousel-item, .clm');
          if (cardItem) {
            target = cardItem;
          }
        }
      }

      if (!target) return;

      // Prevent duplicate rapid-fire pushes on same click event
      var now = Date.now();
      if (now - lastCategoryClickTime < 300) return;
      lastCategoryClickTime = now;

      // Find card item container
      if (!cardItem) {
        cardItem = closest(target, 'ecx-carousel-itm, .js-carousel-item, .carousel-item, .clm') || target;
      }

      // Determine cta_text
      var rawText = attr(target, 'data-ga4-category-link');
      if (!rawText || rawText === 'NA') {
        if (cardItem) rawText = attr(cardItem, 'data-ga4-category-link');
      }
      if (!rawText || rawText === 'NA') {
        var hEl = cardItem ? cardItem.querySelector('h3, .h5, [class*="heading"]') : null;
        if (hEl) rawText = text(hEl);
      }
      if (!rawText || rawText === 'NA') {
        var linkEl = cardItem ? cardItem.querySelector('a[data-ga4-category-link]') : null;
        if (linkEl) rawText = attr(linkEl, 'data-ga4-category-link') || text(linkEl);
      }
      if (!rawText || rawText === 'NA') {
        rawText = text(target);
      }

      // Decode any HTML entities (like &amp; or &#39;) and format cleanly
      var txtNode = document.createElement("textarea");
      txtNode.innerHTML = rawText;
      var cleanText = txtNode.value
        .replace(/[\r\n\t]+/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/\s*&\s*/g, ' & ')
        .replace(/\s+/g, ' ')
        .trim();

      if (!cleanText || cleanText === 'NA') return;

      // Determine destination_url
      var destUrl = '';
      if (target.tagName === 'A' && target.href) {
        destUrl = target.href;
      } else if (cardItem) {
        var anyLink = cardItem.querySelector('a[href]');
        if (anyLink && anyLink.href) {
          destUrl = anyLink.href;
        } else {
          destUrl = attr(cardItem, 'data-ga4-category-url') || attr(target, 'data-ga4-category-url');
        }
      }

      // Normalize destination_url if needed
      if (destUrl && destUrl.indexOf('shopify://collections/') === 0) {
        destUrl = window.location.origin + '/collections/' + destUrl.replace('shopify://collections/', '');
      } else if (destUrl && destUrl.indexOf('/') === 0) {
        destUrl = window.location.origin + destUrl;
      } else if (!destUrl || destUrl === 'NA') {
        destUrl = window.location.href;
      }

      push('category_selection', {
        cta_text: cleanText,
        destination_url: destUrl
      });
    }, true);
  }

  /* ═══════════════════════════════════════════════════════════
     5. category_tab_click  (Shop by Concern & Oral Care Essentials)
     AUDIT FIX: cover both sections (not just Shop by Concern)
  ════════════════════════════════════════════════════════════ */
  function bindCategoryTab() {
    // Click-style tabs (buttons / anchors with data-ga4-tab)
    // AUDIT FIX: use capture phase (true) so the event fires even when
    // ecx-tabs or other tab JS calls stopPropagation during bubbling.
    document.addEventListener('click', function (e) {
      var tab = closest(e.target, '[data-ga4-tab]');
      if (!tab) return;
      var container = closest(tab, '[data-ga4-tab-section]');

      var rawSection = container ? attr(container, 'data-ga4-tab-section') : 'NA';
      var txtNode = document.createElement("textarea");
      txtNode.innerHTML = rawSection;
      var cleanSection = txtNode.value.trim();

      // AUDIT FIX: read attribute value directly — attr() returns 'NA' for
      // empty/bare attributes which prevented fall-through to text(tab).
      var rawTabAttr = (tab.getAttribute('data-ga4-tab') || '').trim();
      var ctaText = rawTabAttr || text(tab);

      push('category_tab_click', {
        section_name: cleanSection,
        cta_text: ctaText
      });
    }, true);

    // Select-style (mobile dropdowns)
    document.addEventListener('change', function (e) {
      var select = closest(e.target, '[data-ga4-tab-select]');
      if (!select) return;
      var container = closest(select, '[data-ga4-tab-section]');

      var rawSection = container ? attr(container, 'data-ga4-tab-section') : 'NA';
      var txtNode = document.createElement("textarea");
      txtNode.innerHTML = rawSection;
      var cleanSection = txtNode.value.trim();

      var selectedText = select.options && select.selectedIndex >= 0
        ? select.options[select.selectedIndex].text.trim()
        : select.value;

      push('category_tab_click', {
        section_name: cleanSection,
        cta_text: selectedText
      });
    }, true);
  }

  /* ═══════════════════════════════════════════════════════════
     6. cta_click  (General CTAs across all sections)
     AUDIT FIX: Must NOT fire on nav links, footer links, category items,
     cart icon clicks, or hamburger button — only real CTAs.
  ════════════════════════════════════════════════════════════ */
  function bindCtaClick() {
    document.addEventListener('click', function (e) {
      // Guard: exclude elements that have their own dedicated events
      if (closest(e.target, [
        '[data-ga4-nav-section]',
        '[data-ga4-hamburger-section]',
        '[data-ga4-footer-section]',
        '[data-ga4-category-link]',
        '[data-ga4-category-section]',
        '.image-with-text-pro',
        '[data-ga4-tab]',
        '[data-ga4-filter-form]',
        '[data-ga4-logo]',
        '[data-ga4-social-name]',
        '[data-ga4-brand-logo]',
        '[data-ga4-faq]',
        '[data-ga4-dropdown]',
        '[data-ga4-clinical]',
        '[data-ga4-pincode-form]',
        '.js-header-cart-status-btn', // cart icon
        '.js-menu-drawer-trigger'     // hamburger open button
      ].join(','))) return;

      var cta = closest(e.target, '[data-ga4-cta]');
      if (!cta) return;

      // Find section name from attribute or nearest heading
      var sectionEl = cta.hasAttribute('data-ga4-cta-section')
        ? cta
        : closest(cta, '[data-ga4-cta-section]');

      var sectionName = 'NA';
      if (sectionEl && sectionEl.getAttribute('data-ga4-cta-section')) {
        sectionName = sectionEl.getAttribute('data-ga4-cta-section');
      } else {
        // Try to get nearest section heading
        var nearestSection = closest(cta, 'section, [data-ui-component], .shopify-section');
        if (nearestSection) {
          var heading = nearestSection.querySelector('h1, h2, h3, [class*="heading"], [class*="title"]');
          if (heading) sectionName = text(heading);
        }
      }

      var rawCtaText = attr(cta, 'data-ga4-cta') || text(cta);
      var txtNode = document.createElement("textarea");
      txtNode.innerHTML = rawCtaText;
      var cleanCtaText = txtNode.value.trim();

      push('cta_click', {
        section_name: sectionName,
        cta_text: cleanCtaText,
        destination_url: cta.href || 'NA'
      });
    }, true);
  }

  /* ═══════════════════════════════════════════════════════════
     7. footer_interaction
     AUDIT FIX: section_name from data-ga4-footer-section (not "NA")
     AUDIT FIX: must NOT fire when filter is being removed
     AUDIT FIX: robust detection — check both data attributes AND
     footer container structure to ensure the event fires.
  ════════════════════════════════════════════════════════════ */
  function bindFooter() {
    document.addEventListener('click', function (e) {
      // Guard: if this is a filter removal, do NOT fire footer_interaction
      if (closest(e.target, '[data-ga4-filter-form], .js-active-filter-remove, [data-ga4-filter-remove], .js-reset-form-btn, .js-collection-product-clear-all-btn')) return;

      // Guard: if this is a social media icon, let social_media_interaction handle it
      if (closest(e.target, '[data-ga4-social-name]')) return;

      var link = closest(e.target, 'a');
      if (!link) return;

      // Strategy 1: Check for data-ga4-footer-section attribute
      var sectionEl = closest(link, '[data-ga4-footer-section]');

      // Strategy 2: If not found, check if we're inside the footer element
      if (!sectionEl) {
        var footer = closest(link, '.js-footer, footer');
        if (!footer) return;

        // We're inside the footer but no data-ga4-footer-section — find section name
        // from a nearby heading (collapsible trigger button or h6)
        var navList = closest(link, '.footer__nav');
        if (navList) {
          var collapsibleContent = closest(navList, '.collapsible-region__content, .js-collapsible-region-content');
          if (collapsibleContent) {
            var collapsibleRegion = collapsibleContent.parentElement;
            if (collapsibleRegion) {
              var trigger = collapsibleRegion.querySelector('.js-collapsible-region-trigger, button, .h6');
              if (trigger) {
                sectionEl = { _fallbackName: text(trigger) };
              }
            }
          }
        }

        // If still no section, try to find from footer column heading
        if (!sectionEl) {
          var footerCol = closest(link, '.clm, [class*="footer"]');
          if (footerCol) {
            var heading = footerCol.querySelector('.h6, h6, [class*="heading"]');
            if (heading) {
              sectionEl = { _fallbackName: text(heading) };
            }
          }
        }

        // If still nothing, it's a generic footer link
        if (!sectionEl) {
          sectionEl = { _fallbackName: 'Footer' };
        }
      }

      var rawSectionName;
      if (sectionEl._fallbackName) {
        rawSectionName = sectionEl._fallbackName;
      } else {
        rawSectionName = attr(sectionEl, 'data-ga4-footer-section') || 'Footer';
      }

      var txtNode1 = document.createElement("textarea");
      txtNode1.innerHTML = rawSectionName;
      var cleanSectionName = txtNode1.value.trim();

      var rawCtaText = text(link);
      var txtNode2 = document.createElement("textarea");
      txtNode2.innerHTML = rawCtaText;
      var cleanCtaText = txtNode2.value.trim();

      push('footer_interaction', {
        section_name: cleanSectionName,
        cta_text: cleanCtaText,
        destination_url: link.href || 'NA'
      });
    }, true);
  }


  /* ═══════════════════════════════════════════════════════════
     8. email_signup_subscription
     AUDIT FIX: section_name always "JOIN THE PERFORA FAMILY"
  ════════════════════════════════════════════════════════════ */
  function bindEmailSignup() {
    function getSectionName(form) {
      var el = form.hasAttribute('data-ga4-email-section')
        ? form
        : closest(form, '[data-ga4-email-section]');
      var rawName = (el && el.getAttribute('data-ga4-email-section')) || '';

      var txtNode = document.createElement("textarea");
      txtNode.innerHTML = rawName;
      var cleanName = txtNode.value.trim();

      if (!cleanName || cleanName.toUpperCase() === 'NA') {
        return 'JOIN THE PERFORA FAMILY';
      }
      return cleanName;
    }

    function checkFormStatus(form) {
      if (form.dataset.ga4EmailTracked) return;

      // Check for success/error elements inside the form
      var isSuccess = form.querySelector('.newsletter-form__message--success, .form-success, .js-success, [id*="-success"], .icon--success');
      var isError = form.querySelector('.newsletter-form__message--error, .form-error, .js-error, [id*="-error"], .icon--error');

      // Shopify native fallback: check URL for customer_posted=true
      if (!isSuccess && !isError) {
        var url = window.location.href;
        if (url.indexOf('customer_posted=true') !== -1) {
          var hash = window.location.hash;
          var formId = form.getAttribute('id') || '';
          if (!hash || hash === '#' + formId || hash.indexOf('Newsletter') !== -1) {
            isSuccess = true;
          }
        }
      }

      if (isSuccess || isError) {
        form.dataset.ga4EmailTracked = "true";
        push('email_signup_subscription', {
          section_name: getSectionName(form),
          selection_type: isSuccess ? 'Success' : 'Failure'
        });
      }
    }

    // 1. Check on page load for native Shopify forms that reload the page
    // Using multiple selectors to ensure we find the form even if Shopify strips the data attribute
    var forms = document.querySelectorAll('[data-ga4-email-form], .footer-newsletter-form, form[action*="/contact"]');
    forms.forEach(checkFormStatus);

    // 2. Global URL fallback check: If we have customer_posted=true but the event didn't fire yet
    if (window.location.href.indexOf('customer_posted=true') !== -1) {
      var isTracked = false;
      forms.forEach(function (f) {
        if (f.dataset.ga4EmailTracked) isTracked = true;
      });

      if (!isTracked) {
        var hash = window.location.hash;
        var targetForm = hash ? document.querySelector(hash) : null;
        if (!targetForm) targetForm = document.querySelector('.footer-newsletter-form, [data-ga4-email-form]');

        if (targetForm) {
          targetForm.dataset.ga4EmailTracked = "true";
          push('email_signup_subscription', {
            section_name: getSectionName(targetForm),
            selection_type: 'Success'
          });
        }
      }
    }

    // 3. Watch for AJAX success/failure messages
    var mo = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (!node || node.nodeType !== 1) return;

          if (node.hasAttribute && node.hasAttribute('data-ga4-email-form')) {
            checkFormStatus(node);
          }
          var form = closest(node, '[data-ga4-email-form], .footer-newsletter-form');
          if (form) {
            checkFormStatus(form);
          }
        });
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  /* ═══════════════════════════════════════════════════════════
     9. social_media_interaction  (NEW — 17-Sep-2026 audit)
     Trigger: Click on social icon links with data-ga4-social-name
     AUDIT FIX: include section_name and destination_url
  ════════════════════════════════════════════════════════════ */
  function bindSocialMedia() {
    document.addEventListener('click', function (e) {
      var link = closest(e.target, '[data-ga4-social-name]');
      if (!link) return;

      // Resolve section_name from nearest footer section or fallback
      var sectionEl = closest(link, '[data-ga4-footer-section]');
      var sectionName = sectionEl ? attr(sectionEl, 'data-ga4-footer-section') : 'NA';
      if (!sectionName || sectionName === 'NA') {
        var footer = closest(link, '.js-footer, footer');
        if (footer) {
          sectionName = 'Social Media';
        } else {
          sectionName = 'NA';
        }
      }

      push('social_media_interaction', {
        section_name: sectionName,
        cta_text: attr(link, 'data-ga4-social-name'),
        destination_url: link.href || 'NA'
      });
    }, true);
  }

  /* ═══════════════════════════════════════════════════════════
     10. faq_click  (FAQ accordions on Collection & Product pages)
     AUDIT FIX: must NOT fire dropdown_interaction for FAQs
     AUDIT FIX: must NOT fire faq_click on PDP product info dropdowns
  ════════════════════════════════════════════════════════════ */
  function bindFaq() {
    document.addEventListener('click', function (e) {
      // Guard: do not fire if inside header navigation
      if (closest(e.target, '.header__nav, .js-header')) return;

      // Strategy 1: Standard <details><summary> FAQs
      var summary = closest(e.target, 'summary');
      if (summary) {
        var details = summary.closest('details');
        if (details) {
          // Guard: Ensure it's not a PDP product info dropdown
          if (details.hasAttribute('data-ga4-dropdown') || closest(details, '.product-accordion')) return;

          // Check if it's explicitly marked or inside an FAQ section container
          var isFaq = details.hasAttribute('data-ga4-faq')
            || details.classList.contains('pfq__item')
            || closest(details, '#faq, .section-faq, .product-faq, .pfq, [data-ui-component*="FAQ"]');

          if (isFaq) {
            var action = details.open ? 'collapse' : 'expand';
            var questionEl = summary.querySelector('span, p, h2, h3, h4') || summary;
            push('faq_click', {
              question_title: text(questionEl).replace(/[\r\n]+/g, ' ').trim(),
              action: action
            });
            return;
          }
        }
      }

      // Strategy 2: Product Page FAQs (.toggle-faq-wraper)
      var faqHeading = closest(e.target, '.faqList__item-heading');
      if (faqHeading) {
        var wrapper = closest(faqHeading, '.toggle-faq-wraper');
        if (wrapper) {
          var titleEl = wrapper.querySelector('.faqList__item-title');
          if (titleEl) {
            var action = wrapper.classList.contains('active') ? 'collapse' : 'expand';
            push('faq_click', {
              question_title: text(titleEl).replace(/[\r\n]+/g, ' ').trim(),
              action: action
            });
            return;
          }
        }
      }

      // Strategy 3: FAQ Navigator (.fn-question)
      var fnQuestion = closest(e.target, '.fn-question');
      if (fnQuestion) {
        // aria-expanded reflects the state before toggle in capture phase? Actually, 
        // the native script might toggle it on click. Using capture phase, we read the current state.
        var isExpanded = fnQuestion.getAttribute('aria-expanded') === 'true';
        var action = isExpanded ? 'collapse' : 'expand';
        var titleEl = fnQuestion.querySelector('.fn-question__text') || fnQuestion;

        push('faq_click', {
          question_title: text(titleEl).replace(/[\r\n]+/g, ' ').trim(),
          action: action
        });
        return;
      }
    }, true);
  }

  /* ═══════════════════════════════════════════════════════════
     11. dropdown_interaction  (PDP "How To Use", "Benefits" etc.)
     AUDIT FIX: must NOT fire faq_click for these accordions
  ════════════════════════════════════════════════════════════ */
  function bindDropdown() {
    document.addEventListener('click', function (e) {
      var trigger = closest(e.target, '.js-accordion-trigger');
      if (!trigger) return;

      // Guard: Do not fire on FAQs
      if (closest(trigger, '.pfq__item, [data-ga4-faq]')) return;

      var isExpanded = trigger.getAttribute('aria-expanded') === 'true';
      var action = isExpanded ? 'collapse' : 'expand';

      var prodName = (window.PerforaGA4 && window.PerforaGA4.product && window.PerforaGA4.product.item_name) ||
        text(document.querySelector('h1.product__title, .product-title, h1')) ||
        'NA';

      var ctaText = 'NA';
      var titleSpan = trigger.querySelector('.h5, .h6, .ta-left, .accordion__title');
      if (titleSpan) {
        ctaText = text(titleSpan);
      } else {
        // Fallback: grab all text but ignore the +/- toggle icons
        var clone = trigger.cloneNode(true);
        var toggle = clone.querySelector('.accordion__toggle, .icon');
        if (toggle) toggle.parentNode.removeChild(toggle);
        ctaText = text(clone);
      }
      ctaText = ctaText.replace(/[\r\n]+/g, ' ').trim() || 'NA';

      push('dropdown_interaction', {
        product_name: prodName,
        cta_text: ctaText,
        action: action
      });
    }, true);
  }

  /* ═══════════════════════════════════════════════════════════
     12. pack_size_select
     AUDIT FIX: must trigger on Homepage & Collection Page cards
     (keep existing PDP implementation, extend to other pages)
  ════════════════════════════════════════════════════════════ */
  function bindPackSize() {
    function getProductName(el) {
      return attr(el, 'data-ga4-pack-product') ||
        (window.PerforaGA4 && window.PerforaGA4.product && window.PerforaGA4.product.item_name) ||
        text(document.querySelector('h1.product__title, .product-title, h1')) ||
        'NA';
    }

    var lastPackTime = 0;
    var lastPackVal = '';

    // Button/label clicks (pack pills)
    document.addEventListener('click', function (e) {
      var btn = closest(e.target, '[data-ga4-pack-size]');
      if (!btn) return;

      var appliedVal = attr(btn, 'data-ga4-pack-size') || text(btn);
      var now = Date.now();
      if (now - lastPackTime < 100 && lastPackVal === appliedVal) {
        return;
      }
      lastPackTime = now;
      lastPackVal = appliedVal;

      var sectionEl = closest(btn, '[data-ga4-pack-section], [data-ga4-section]');
      var sectionName = sectionEl ? (attr(sectionEl, 'data-ga4-pack-section') || attr(sectionEl, 'data-ga4-section')) : 'NA';

      push('pack_size_select', {
        section_name: sectionName,
        product_name: getProductName(btn),
        applied_value: appliedVal
      });
    }, true);

    // Select dropdown (variant select)
    document.addEventListener('change', function (e) {
      var select = closest(e.target, '[data-ga4-pack-select]');
      if (!select) return;

      var sectionEl = closest(select, '[data-ga4-pack-section], [data-ga4-section]');
      var sectionName = sectionEl ? (attr(sectionEl, 'data-ga4-pack-section') || attr(sectionEl, 'data-ga4-section')) : 'NA';
      var selectedText = select.options && select.selectedIndex >= 0
        ? select.options[select.selectedIndex].text.trim()
        : select.value;

      push('pack_size_select', {
        section_name: sectionName,
        product_name: getProductName(select),
        applied_value: selectedText
      });
    }, true);
  }

  /* ═══════════════════════════════════════════════════════════
     13. view_clinical_reports_click
     AUDIT FIX: works on both d-web and m-web
  ════════════════════════════════════════════════════════════ */
  function bindClinicalReports() {
    document.addEventListener('click', function (e) {
      var cta = closest(e.target, '[data-ga4-clinical], .view-clinical-text, .view-clinical-text a, .view-clinical-btn, .clinical-reports-btn, #purple-serum-clinical-modal-btn');
      if (!cta) return;
      var prodName = attr(cta, 'data-ga4-clinical-product') ||
        (window.PerforaGA4 && window.PerforaGA4.product && window.PerforaGA4.product.item_name) ||
        text(document.querySelector('h1.product__title, .product-title, h1')) ||
        'NA';
      push('view_clinical_reports_click', {
        product_name: prodName
      });
    }, true);
  }

  /* ═══════════════════════════════════════════════════════════
     14. filter_applied / filter_removed / sort_applied
     AUDIT FIX: must NOT also fire faq_click, cta_click, footer_interaction
     — achieved by NOT delegating through those selectors from filter events.
  ════════════════════════════════════════════════════════════ */
  function getFilterGroupLabel(inp) {
    if (!inp) return 'NA';

    // 0. Explicit attribute on the input or its parent container
    var explicit = inp.getAttribute('data-ga4-filter-group') || inp.getAttribute('data-filter-group');
    if (explicit) return explicit;
    var containerWithGroup = closest(inp, '[data-ga4-filter-group], [data-filter-group]');
    if (containerWithGroup) {
      var g = containerWithGroup.getAttribute('data-ga4-filter-group') || containerWithGroup.getAttribute('data-filter-group');
      if (g) return g;
    }

    // 1. SearchTap query param name pattern: e.g. f.Product Type -> "Product Type"
    var name = inp.name || '';
    if (name.indexOf('f.') === 0) {
      return name.replace(/^f\./, '').trim();
    }

    // 2. SearchTap filters / facets (e.g. on /pages/searchtap-search)
    var stBlock = closest(inp, '.st-filter-block, .st-facet, .st-filter-group, [class*="filter-group"], [class*="filter-block"], .st-filter-section, .st-facet-wrapper');
    if (stBlock && !stBlock.matches('#st-filter-sidebar, .st-search-container')) {
      var stTitle = stBlock.querySelector('.st-filter-title, .st-facet-title, [class*="filter-title"], [class*="facet-title"], h3, h4, h5, .st-title');
      if (stTitle) {
        var stText = text(stTitle).trim();
        if (stText && stText.toLowerCase() !== 'filter' && stText.toLowerCase() !== 'filters' && stText.toLowerCase() !== 'clear all') {
          return stText;
        }
      }
    }
    var cur = inp.parentElement;
    while (cur && cur !== document.body && !cur.matches('.st-search-container, #st-filter-sidebar, body')) {
      var heading = cur.querySelector('.st-filter-title, .st-facet-title');
      if (heading) {
        var hText = text(heading).trim();
        if (hText && hText.toLowerCase() !== 'filter' && hText.toLowerCase() !== 'clear all') {
          return hText;
        }
      }
      cur = cur.parentElement;
    }

    // 3. Standard Shopify <details>
    var detailsEl = closest(inp, 'details');
    if (detailsEl) {
      var summaryEl = detailsEl.querySelector('summary');
      if (summaryEl) {
        var clone = summaryEl.cloneNode(true);
        Array.prototype.forEach.call(clone.querySelectorAll('svg, button, .accordion__toggle, [class*="icon"], [class*="arrow"]'), function (x) {
          x.remove();
        });
        var rawText = text(clone);
        var txtNode = document.createElement("textarea");
        txtNode.innerHTML = rawText;
        var cleanText = txtNode.value.split('\n')[0].replace(/[\r\n\t]+/g, ' ').trim();
        if (cleanText) return cleanText;
      }
    }

    // 4. Tag / generic fallback
    if (name.indexOf('filter.p.tag') !== -1 || name === 'tag') {
      return 'More filters';
    }
    var nameParts = name.split('.');
    var lastPart = nameParts[nameParts.length - 1] || name || 'filter';
    if (lastPart.toLowerCase() === 'tag') {
      return 'More filters';
    }
    return lastPart;
  }

  function buildFilterPayload(form, changedInput) {
    var isSearch = pageType() === 'search_page';
    var formEl = form || (changedInput && closest(changedInput, '[data-ga4-filter-form], .filter-form, #st-filter-sidebar, .st-search-container')) || document.querySelector('[data-ga4-filter-form]');
    var collectionName = formEl ? attr(formEl, 'data-ga4-collection-name') : 'NA';
    if (!collectionName || collectionName === 'NA') {
      var h1 = document.querySelector('h1.collection-title, h1.page-title, h1');
      if (h1 && !isSearch) collectionName = text(h1);
    }

    // When a single checkbox/radio is clicked
    if (changedInput && (changedInput.type === 'checkbox' || changedInput.type === 'radio')) {
      var key = getFilterGroupLabel(changedInput);
      var label = '';
      if (changedInput.dataset && changedInput.dataset.label) {
        label = changedInput.dataset.label;
      } else if (changedInput.getAttribute('aria-label')) {
        label = changedInput.getAttribute('aria-label');
      } else {
        var lblEl = closest(changedInput, 'label') || (changedInput.id ? document.querySelector('label[for="' + changedInput.id + '"]') : null);
        if (lblEl) {
          var cloneLbl = lblEl.cloneNode(true);
          Array.prototype.forEach.call(cloneLbl.querySelectorAll('input, svg, [class*="count"], span.count'), function (n) { n.remove(); });
          label = text(cloneLbl).trim();
        }
        if (!label) {
          label = changedInput.value || 'NA';
        }
      }
      var cleanLabel = (label || '').replace(/\s*\([0-9]+\)\s*$/, '').trim();

      // On search page, section_name is the filter group name (e.g. "Product Type")
      var secName = isSearch ? (key !== 'NA' ? key : 'Product Type') : (collectionName !== 'NA' ? collectionName : key);

      return {
        section_name: secName || 'NA',
        filter_type: key || 'NA',
        filter_value: cleanLabel || 'NA'
      };
    }

    // Price input interaction
    if (changedInput && (changedInput.name || '').indexOf('price') !== -1) {
      var minEl = formEl && formEl.querySelector('input[name*="price"][name*="gte"], .js-price-gte');
      var maxEl = formEl && formEl.querySelector('input[name*="price"][name*="lte"], .js-price-lte');
      var pVal = (minEl ? minEl.value : '0') + '-' + (maxEl ? maxEl.value : 'NA');
      var secName = isSearch ? 'Price' : (collectionName !== 'NA' ? collectionName : 'Price');
      return {
        section_name: secName,
        filter_type: 'Price',
        filter_value: pVal
      };
    }

    // Fallback: all checked inputs in container
    var inputs = formEl ? formEl.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked') : [];
    var typeMap = {};

    Array.prototype.forEach.call(inputs, function (inp) {
      var k = getFilterGroupLabel(inp);
      var lbl = inp.dataset && inp.dataset.label ? inp.dataset.label : (inp.getAttribute('aria-label') || inp.value);
      var cleanLbl = (lbl || '').replace(/\s*\([0-9]+\)\s*$/, '').trim();
      if (!typeMap[k]) typeMap[k] = [];
      typeMap[k].push(cleanLbl);
    });

    var filterTypes = Object.keys(typeMap).join('|');
    var filterValues = Object.keys(typeMap).map(function (k) { return typeMap[k].join('|'); }).join('|');

    var primaryKey = Object.keys(typeMap)[0] || 'NA';
    var secName = isSearch ? (primaryKey !== 'NA' ? primaryKey : 'Product Type') : (collectionName !== 'NA' ? collectionName : 'NA');

    return {
      section_name: secName || 'NA',
      filter_type: filterTypes || 'NA',
      filter_value: filterValues || 'NA'
    };
  }

  function bindFilterSort() {
    // 1. Filter checkbox / radio change
    document.addEventListener('change', function (e) {
      var target = e.target;
      var isCheckbox = target.type === 'checkbox' || target.type === 'radio';
      var isPriceInput = (target.name || '').indexOf('price') !== -1 || (target.className && target.className.indexOf('price') !== -1);

      if (!isCheckbox && !isPriceInput) return;

      var filterContainer = closest(target, '[data-ga4-filter-form], .filter-form, #st-filter-sidebar, .st-search-container, .st-filter-block, [class*="st-filter"], details');
      if (!filterContainer && !closest(target, 'details.filter-form__details, details')) return;

      if (isCheckbox) {
        if (target.checked) {
          push('filter_applied', buildFilterPayload(filterContainer, target));
        } else {
          // When removed, track the EXACT filter that was removed
          var label = target.dataset && target.dataset.label ? target.dataset.label : (target.getAttribute('aria-label') || target.value);
          var cleanLabel = (label || '').replace(/\s*\([0-9]+\)\s*$/, '').trim();
          var key = getFilterGroupLabel(target);
          var isSearch = pageType() === 'search_page';
          var formEl = filterContainer || document.querySelector('[data-ga4-filter-form]');
          var collectionName = formEl ? attr(formEl, 'data-ga4-collection-name') : 'NA';
          var secName = isSearch ? (key !== 'NA' ? key : 'Product Type') : (collectionName !== 'NA' ? collectionName : key);

          push('filter_removed', {
            section_name: secName,
            filter_type: key,
            filter_value: cleanLabel,
            cta_text: cleanLabel
          });
        }
      } else if (isPriceInput) {
        clearTimeout(filterContainer && filterContainer._priceTimer);
        var timer = setTimeout(function () {
          push('filter_applied', buildFilterPayload(filterContainer, target));
        }, 700);
        if (filterContainer) filterContainer._priceTimer = timer;
      }
    });

    // 2. Filter removal via Tags (Clear Individual) or Clear All button
    document.addEventListener('click', function (e) {
      var resetBtn = closest(e.target, '.js-reset-form-btn, .js-collection-product-clear-all-btn, [class*="st-clear"], [class*="st-bus-item"], #st-clear-all');
      if (!resetBtn) return;

      var isClearAll = resetBtn.classList.contains('btn-link') ||
        resetBtn.classList.contains('js-collection-product-clear-all-btn') ||
        (resetBtn.id && resetBtn.id.indexOf('clear') !== -1) ||
        /clear\s*all/i.test(text(resetBtn));

      var isSearch = pageType() === 'search_page';
      var form = document.querySelector('[data-ga4-filter-form]');
      var collectionName = form ? attr(form, 'data-ga4-collection-name') : 'NA';
      var secName = isSearch ? 'Product Type' : (collectionName !== 'NA' ? collectionName : 'NA');

      if (isClearAll) {
        push('filter_removed', {
          section_name: secName,
          filter_type: 'NA',
          filter_value: 'Clear all',
          cta_text: 'Clear all'
        });
      } else {
        var ctaTxt = text(resetBtn).replace(/[\r\n]+/g, ' ').trim() || 'NA';
        push('filter_removed', {
          section_name: secName,
          filter_type: attr(resetBtn, 'data-ga4-filter-type') || 'NA',
          filter_value: attr(resetBtn, 'data-ga4-filter-value') || ctaTxt,
          cta_text: ctaTxt
        });
      }
    }, true);

    // 3. Sort select change
    document.addEventListener('change', function (e) {
      var select = closest(e.target, '[data-ga4-sort-select], select[name="sort_by"], .js-collection-product-sort-by-field-select, #st-sort select, .st-sorting-select');
      if (!select) return;

      var selectedText = select.options && select.selectedIndex >= 0
        ? select.options[select.selectedIndex].text.trim()
        : select.value;

      var isSearch = pageType() === 'search_page';
      var formEl = document.querySelector('[data-ga4-collection-name]');
      var collectionName = formEl ? attr(formEl, 'data-ga4-collection-name') : 'NA';
      var secName = isSearch ? 'Product Type' : (collectionName !== 'NA' ? collectionName : 'NA');

      push('sort_applied', {
        section_name: secName,
        applied_value: selectedText || select.value
      });
    });

    // Active filter tag removal (individual pill "x" clicks)
    document.addEventListener('click', function (e) {
      var removeTag = closest(e.target, '.js-active-filter-remove, [data-ga4-filter-remove]');
      if (!removeTag) return;
      e.stopPropagation();
      var labelEl = removeTag.querySelector('span') || removeTag;
      var isSearch = pageType() === 'search_page';
      var form = document.querySelector('[data-ga4-filter-form]');
      var collectionName = form ? attr(form, 'data-ga4-collection-name') : 'NA';
      var secName = isSearch ? 'Product Type' : (collectionName !== 'NA' ? collectionName : 'NA');

      push('filter_removed', {
        section_name: secName,
        filter_type: attr(removeTag, 'data-filter-type') || 'NA',
        filter_value: text(labelEl)
      });
    });

    // "Clear All" filters button
    document.addEventListener('click', function (e) {
      var clearAll = closest(e.target, '[data-ga4-filter-clear-all], .js-clear-all-filters, .filter-clear-all');
      if (!clearAll) return;
      var isSearch = pageType() === 'search_page';
      var form = document.querySelector('[data-ga4-filter-form]');
      var collectionName = form ? attr(form, 'data-ga4-collection-name') : 'NA';
      var secName = isSearch ? 'Product Type' : (collectionName !== 'NA' ? collectionName : 'NA');

      push('filter_removed', {
        section_name: secName,
        filter_type: 'NA',
        filter_value: 'Clear All'
      });
    }, true);
  }

  /* ═══════════════════════════════════════════════════════════
     15. select_collection_category  (Collection page category pills)
  ════════════════════════════════════════════════════════════ */
  function bindCollectionCategory() {
    document.addEventListener('click', function (e) {
      var pill = closest(e.target, '[data-ga4-collection-cat]');
      if (!pill) return;
      var container = closest(pill, '[data-ga4-collection-name]');
      push('select_collection_category', {
        collection_name: container ? attr(container, 'data-ga4-collection-name') : 'NA',
        cta_text: attr(pill, 'data-ga4-collection-cat') || text(pill)
      });
    }, true);
  }

  /* ═══════════════════════════════════════════════════════════
     16. search_interaction
     Handles: SearchTap (POPULAR CHOICES, RECOMMENDED FOR YOU, autocomplete suggestions),
              native search, manual search submit, Enter key, and search button clicks.
     Schema:
       event: 'search_interaction'
       page_type: 'home_page' | 'collection_page' | 'product_page' | 'others' etc.
       platform: 'd-web' | 'm-web'
       section_name: 'POPULAR CHOICES' | 'RECOMMENDED FOR YOU' | 'NA' | etc.
       selection_type: 'Suggested' | 'Manual'
       cta_text: string
       user_id_event: string | undefined
       user_type: 'logged_in' | 'guest'
       search_term: string
  ════════════════════════════════════════════════════════════ */
  function bindSearch() {
    var lastSearchTerm = '';
    var lastSearchTime = 0;
    var lastSuggestedTerm = '';
    var lastSuggestedTime = 0;

    function pushManual(term) {
      var trimmed = (term || '').trim();
      var now = Date.now();
      if (trimmed === lastSearchTerm && (now - lastSearchTime) < 800) return;
      lastSearchTerm = trimmed;
      lastSearchTime = now;

      push('search_interaction', {
        section_name: 'NA',
        selection_type: 'Manual',
        search_term: trimmed || 'NA',
        cta_text: 'NA'
      });
    }

    function pushSuggested(secName, term, cta) {
      var trimmed = (term || '').trim();
      var now = Date.now();
      if (trimmed === lastSuggestedTerm && (now - lastSuggestedTime) < 600) return;
      lastSuggestedTerm = trimmed;
      lastSuggestedTime = now;

      push('search_interaction', {
        section_name: secName || 'Suggested',
        selection_type: 'Suggested',
        search_term: trimmed || 'NA',
        cta_text: (cta || trimmed || 'NA').trim()
      });
    }

    function cleanHeading(el) {
      if (!el) return 'NA';
      var raw = (el.textContent || el.innerText || '').replace(/[\r\n\t]+/g, ' ').trim();
      var cleaned = raw.replace(/^[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\s\-_•|→←↑↓]+/u, '').trim();
      var upper = cleaned.toUpperCase();
      if (upper.indexOf('POPULAR') !== -1) return 'POPULAR CHOICES';
      if (upper.indexOf('RECOMMENDED') !== -1) return 'RECOMMENDED FOR YOU';
      if (upper.indexOf('RECENT') !== -1) return 'RECENT SEARCHES';
      if (upper.indexOf('TRENDING') !== -1) return 'TRENDING SEARCHES';
      if (upper.indexOf('PRODUCT') !== -1) return 'PRODUCTS';
      if (upper.indexOf('COLLECTION') !== -1) return 'COLLECTIONS';
      if (upper.indexOf('SUGGEST') !== -1) return 'SUGGESTIONS';
      return cleaned || 'NA';
    }

    function findSection(el, container) {
      var explicit = closest(el, '[data-ga4-search-section], [data-ga4-list-name]');
      if (explicit) {
        var v = attr(explicit, 'data-ga4-search-section') || attr(explicit, 'data-ga4-list-name');
        if (v && v !== 'NA') return v;
      }

      var cur = el;
      while (cur && cur !== container && cur !== document.body) {
        var h = cur.querySelector && cur.querySelector('h1, h2, h3, h4, h5, h6, .st-title, .st-header, .st-heading, [class*="section-title"], [class*="header-title"]');
        if (h && !h.contains(el)) {
          var hName = cleanHeading(h);
          if (hName !== 'NA') return hName;
        }

        var prev = cur.previousElementSibling;
        while (prev) {
          if (prev.matches && prev.matches('h1, h2, h3, h4, h5, h6, .st-title, .st-header, .st-heading, [class*="section-title"], [class*="header-title"]')) {
            var sName = cleanHeading(prev);
            if (sName !== 'NA') return sName;
          }
          var subH = prev.querySelector && prev.querySelector('h1, h2, h3, h4, h5, h6, .st-title, .st-header, .st-heading, [class*="section-title"], [class*="header-title"]');
          if (subH) {
            var subName = cleanHeading(subH);
            if (subName !== 'NA') return subName;
          }
          prev = prev.previousElementSibling;
        }

        cur = cur.parentElement;
      }

      return 'NA';
    }

    function cleanTerm(str) {
      if (!str) return 'NA';
      return str.replace(/[→←↑↓]/g, '')
                .replace(/^[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\s\-_•|]+/u, '')
                .replace(/\s+/g, ' ')
                .trim() || 'NA';
    }

    function getSearchInput(root) {
      if (root && root.querySelector) {
        var inp = root.querySelector('input[type="search"], input[name="q"], .st-search-input, [data-ga4-search-input], input');
        if (inp) return inp;
      }
      return document.querySelector('#st-search, .st-search-input, [data-ga4-search-input], form[action*="/search"] input[name="q"]');
    }

    // 1. Click Listener (capture phase)
    document.addEventListener('click', function (e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      // ── A. Explicit data-ga4-search-result
      var explicitResult = closest(e.target, '[data-ga4-search-result]');
      if (explicitResult) {
        var ctnr = closest(explicitResult, '[data-ga4-search-section]');
        var t = attr(explicitResult, 'data-ga4-search-result') || text(explicitResult);
        pushSuggested(ctnr ? attr(ctnr, 'data-ga4-search-section') : 'Suggested', t, t);
        return;
      }

      // ── B. SearchTap Containers (#st-autocomplete-container, .st-search-root, .st-autocomplete-container, etc.)
      var stRoot = closest(e.target, '#st-autocomplete-container, .st-search-root, .st-autocomplete-container, .st-mobile-search-container, #st-search-container, #st-before-search, #st-after-search');
      if (stRoot) {
        // Ignore back arrow / close buttons / clear buttons
        if (closest(e.target, '.st-back, .st-back-arrow, [class*="back-arrow"], [class*="close-btn"], [class*="modal-close"], .st-clear, [class*="clear-btn"], .st-autocomplete-close-btn')) {
          return;
        }

        // Check if user clicked the search magnifier / submit button inside SearchTap
        var searchBtn = closest(e.target, '.st-search-btn, .st-search-icon, [class*="search-icon"], [class*="magnifier"], button[type="submit"]');
        if (searchBtn && !searchBtn.classList.contains('st-autocomplete-open-btn')) {
          var inp = getSearchInput(stRoot);
          if (inp && inp.value.trim()) {
            pushManual(inp.value.trim());
            return;
          }
        }

        // B1. Product Card in SearchTap (e.g. RECOMMENDED FOR YOU)
        var stCard = closest(e.target, '.st-product-card, [data-st-product-id], [data-st-product-name], .st-outer-box');
        if (stCard) {
          var prodName = attr(stCard, 'data-st-product-name') || attr(stCard, 'data-ga4-item-name');
          if (!prodName || prodName === 'NA') {
            var titleEl = stCard.querySelector('.st-name, .product-title, [class*="product-name"], [class*="title"], h3, h4, a');
            if (titleEl) prodName = text(titleEl);
          }
          if (!prodName || prodName === 'NA') {
            prodName = text(stCard);
          }
          prodName = cleanTerm(prodName);

          var sName = findSection(stCard, stRoot);
          if (sName === 'NA') {
            sName = closest(stCard, '#st-before-search, .st-recommended-cards, #st-product-wrapper') ? 'RECOMMENDED FOR YOU' : 'RECOMMENDED FOR YOU';
          }
          pushSuggested(sName, prodName, prodName);
          return;
        }

        // B2. Popular Choice pill / button / link / query suggestion
        var pill = closest(e.target, 'a, button, li, [class*="popular"], [class*="tag"], [class*="pill"], [class*="choice"], [class*="query"], [class*="item"]') ||
                   (closest(e.target, '#st-before-search') ? closest(e.target, '#st-before-search > div > div, #st-before-search div') : null);
        if (pill && pill !== stRoot && !pill.contains(stRoot)) {
          var pillText = cleanTerm(text(pill));
          if (pillText && pillText !== 'NA') {
            var pSecName = findSection(pill, stRoot);
            if (pSecName === 'NA' && closest(pill, '#st-before-search')) {
              pSecName = 'POPULAR CHOICES';
            }
            pushSuggested(pSecName !== 'NA' ? pSecName : 'POPULAR CHOICES', pillText, pillText);
            return;
          }
        }
      }

      // ── C. Native predictive search (ecx-predictive-srch, [data-ga4-search-form])
      var link = closest(e.target, 'a');
      var searchApp = closest(e.target, 'ecx-predictive-srch, [data-ga4-search-form]');
      if (link && searchApp) {
        if (link.classList.contains('js-predictive-search-close-trigger')) return;

        // View all / manual link
        if (link.classList.contains('btn-link') || (link.getAttribute('href') && link.getAttribute('href').indexOf('/search') === 0)) {
          var inputEl = getSearchInput(searchApp);
          pushManual(inputEl ? inputEl.value.trim() : 'NA');
          return;
        }

        var itemWrapper = closest(e.target, '[data-ga4-item-name]');
        var searchTerm = itemWrapper ? attr(itemWrapper, 'data-ga4-item-name') : text(link);
        var listWrapper = closest(e.target, '[data-ga4-list-name]');
        var sec = listWrapper ? attr(listWrapper, 'data-ga4-list-name') : 'NA';

        if (sec === 'NA') {
          var headingContainer = closest(link, '.search__result--collections, .search__result--articles, .search__result--page');
          if (headingContainer) {
            var h2 = headingContainer.querySelector('h2');
            if (h2) sec = text(h2).replace(/\([0-9]+\)/g, '').trim();
          } else {
            sec = 'Suggested';
          }
        }

        searchTerm = cleanTerm(searchTerm);
        if (searchTerm && searchTerm !== 'NA') {
          pushSuggested(sec, searchTerm, searchTerm);
        }
      }
    }, true);

    // 2. Manual search: form submit
    document.addEventListener('submit', function (e) {
      var form = closest(e.target, '[data-ga4-search-form], form[action*="/search"]');
      if (!form) return;
      var input = getSearchInput(form);
      pushManual(input ? input.value.trim() : 'NA');
    }, true);

    // 3. Enter key on search input (Desktop + Mobile SearchTap + Theme inputs)
    document.addEventListener('keyup', function (e) {
      if (e.key !== 'Enter') return;
      var input = closest(e.target, '#st-search, .st-search-input, .st-search-root input, #st-autocomplete-container input, .st-mobile-search-container input, [data-ga4-search-input], [data-ga4-search-form] input, form[action*="/search"] input[name="q"], input[type="search"]');
      if (!input) return;
      pushManual(input.value.trim());
    }, true);
  }

  /* ═══════════════════════════════════════════════════════════
     17. scroll_depth
     AUDIT FIX: scroll_depth must be an INTEGER, not a string
  ════════════════════════════════════════════════════════════ */
  function bindScrollDepth() {
    var milestones = [25, 50, 75, 90];
    var fired = {};

    function getScrollPercent() {
      var scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
      var docHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.offsetHeight
      ) - window.innerHeight;
      if (docHeight <= 0) return 0;
      return Math.min(100, Math.round((scrollTop / docHeight) * 100));
    }

    var ticking = false;
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var pct = getScrollPercent();
        milestones.forEach(function (m) {
          if (!fired[m] && pct >= m) {
            fired[m] = true;
            push('scroll_depth', {
              scroll_depth: parseInt(m, 10)
            });
          }
        });
        ticking = false;
      });
    }, { passive: true });
  }

  /* ═══════════════════════════════════════════════════════════
     18. pincode_interaction
     Trigger: user submits pincode check form
  ════════════════════════════════════════════════════════════ */
  function bindPincode() {
    // 1. Listen for the new exact custom event dispatched from snippets/pincodevalidator.liquid
    document.addEventListener('ga4_pincode_result', function (e) {
      var prodName = (window.PerforaGA4 && window.PerforaGA4.product && window.PerforaGA4.product.item_name) ||
        text(document.querySelector('h1.product__title, .product-title, h1')) ||
        'NA';

      push('pincode_interaction', {
        product_name: prodName,
        deliverable_type: e.detail.deliverable_type || 'NA',
        deliverable: e.detail.deliverable || 'False'
      });
    });

    // 2. Original fallback listeners for any other pincode modules
    document.addEventListener('click', function (e) {
      var btn = closest(e.target, '[data-ga4-pincode-form], .js-pincode-check-btn, .pincode-check-submit');
      if (!btn) return;

      // Ignore clicks from the custom validator snippet because it dispatches a precise event now
      if (closest(btn, '.pincodevalidator-wrap')) return;

      var prodName = attr(btn, 'data-ga4-pincode-product') ||
        (window.PerforaGA4 && window.PerforaGA4.product && window.PerforaGA4.product.item_name) ||
        text(document.querySelector('h1.product__title, .product-title, h1')) ||
        'NA';

      // Determine deliverability from response message (if visible)
      var resultEl = document.querySelector('.js-pincode-result, .pincode-result, [class*="pincode-message"]');
      var deliverable = 'NA';
      var deliverableType = 'NA';
      if (resultEl) {
        var resultText = text(resultEl).toLowerCase();
        deliverable = (resultText.indexOf('deliverable') !== -1 || resultText.indexOf('available') !== -1) ? 'True' : 'False';
        deliverableType = (resultText.indexOf('free') !== -1) ? 'Free' : 'Chargeable';
      }

      push('pincode_interaction', {
        product_name: prodName,
        deliverable_type: deliverableType,
        deliverable: deliverable
      });
    });

    // Also watch for pincode result appearing via AJAX
    var mo = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (!node || node.nodeType !== 1) return;
          // Ignore mutations from our updated snippet
          if (closest(node, '.pincodevalidator-wrap')) return;

          var isResult = (node.classList && (
            node.classList.contains('js-pincode-result') ||
            node.classList.contains('pincode-result')
          )) || (node.querySelector && node.querySelector('.js-pincode-result, .pincode-result'));
          if (!isResult) return;
          var prodName = (window.PerforaGA4 && window.PerforaGA4.product && window.PerforaGA4.product.item_name) ||
            text(document.querySelector('h1.product__title, .product-title, h1')) ||
            'NA';
          var resultText = text(node).toLowerCase();
          var deliverable = (resultText.indexOf('deliverable') !== -1 || resultText.indexOf('available') !== -1) ? 'True' : 'False';
          var deliverableType = (resultText.indexOf('free') !== -1) ? 'Free' : 'Chargeable';
          push('pincode_interaction', {
            product_name: prodName,
            deliverable_type: deliverableType,
            deliverable: deliverable
          });
        });
      });
    });
    mo.observe(document.body, { childList: true, subtree: true }, true);
  }

  /* ═══════════════════════════════════════════════════════════
     19. video_interaction
     AUDIT FIX: only fire for open, close, mute, unmute actions
     (NOT for every video state change like progress, timeupdate, etc.)
  ════════════════════════════════════════════════════════════ */
  function bindVideoInteraction() {
    function getVideoProduct(el) {
      var container = closest(el, '[data-ga4-video-product]');
      return (container && container.getAttribute('data-ga4-video-product')) ||
        (window.PerforaGA4 && window.PerforaGA4.product && window.PerforaGA4.product.item_name) ||
        text(document.querySelector('h1.product__title, .product-title, h1')) ||
        'NA';
    }

    var lastVideoInteraction = { time: 0, action: '', product: '' };

    function fireVideoEvent(el, action) {
      var prod = getVideoProduct(el);
      var now = Date.now();
      
      // Debounce duplicate identical actions across the same product (e.g. click + volumechange)
      if (lastVideoInteraction.action === action && lastVideoInteraction.product === prod && (now - lastVideoInteraction.time < 1000)) {
        return;
      }
      lastVideoInteraction = { time: now, action: action, product: prod };

      push('video_interaction', {
        product_name: prod,
        action: action
      });
    }

    // Click-based: open modal / play button / close button / mute button
    document.addEventListener('click', function (e) {
      var btn = e.target;

      // Video close
      var closeBtn = closest(btn, '[data-ga4-video-close], .js-video-close, .video-modal-close, .js-close-video, [class*="close-video"]');
      if (closeBtn) {
        fireVideoEvent(closeBtn, 'close');
        
        // Reset open state for any inline play buttons so they can fire open again
        var modal = closest(btn, '.video-modal, .modal-video, [class*="video-modal"]');
        if (modal && modal._ga4VideoOpenedBtn) {
          modal._ga4VideoOpenedBtn.removeAttribute('data-ga4-video-state');
        }
        return;
      }

      // Mute / unmute custom buttons
      var muteBtn = closest(btn, '[data-ga4-video-mute], .js-video-mute, .video-mute-btn, [class*="mute"]');
      // Ensure it's not a native video element being clicked
      if (muteBtn && muteBtn.tagName !== 'VIDEO') {
        var action = muteBtn.getAttribute('data-ga4-video-muted') === 'true' ? 'unmute' : 'mute';
        muteBtn.setAttribute('data-ga4-video-muted', action === 'mute' ? 'true' : 'false');
        fireVideoEvent(muteBtn, action);
        return;
      }

      // Ignore clicks directly on the native video player (e.g. play/pause surface)
      // to prevent spamming 'open' on every play/pause interaction
      if (btn.tagName === 'VIDEO') return;

      // Video open: clicking a play or open CTA for a video
      var openBtn = closest(btn, '[data-ga4-video-open], .js-video-open, .video-play-btn, .video-modal-trigger, [class*="video-play"]');
      if (openBtn) {
        // Prevent duplicate 'open' fires if the user clicks inside an already open video modal
        var modal = closest(btn, '.video-modal, .modal-video, [class*="video-modal"]');
        if (modal && modal.classList.contains('is-open')) {
          return;
        }

        // Also prevent firing 'open' repeatedly for inline play buttons
        if (openBtn.getAttribute('data-ga4-video-state') === 'open') return;
        openBtn.setAttribute('data-ga4-video-state', 'open');
        
        if (modal) modal._ga4VideoOpenedBtn = openBtn; // Link it so close can reset it

        fireVideoEvent(openBtn, 'open');
        return;
      }
    }, true); // Use capture phase to ensure it catches clicks before UI disappears

    // Native <video> mute/unmute via volumechange event
    document.addEventListener('volumechange', function (e) {
      var vid = e.target;
      if (!vid || vid.tagName !== 'VIDEO') return;

      var isMuted = vid.muted || vid.volume === 0;
      var wasMuted = vid.getAttribute('data-ga4-last-muted') === 'true';

      // Only fire when the mute state actually flips (prevents spam on volume slider drag)
      if (isMuted !== wasMuted) {
        vid.setAttribute('data-ga4-last-muted', isMuted ? 'true' : 'false');
        
        // Don't fire mute blindly on load if the video starts muted (unless they actually click it)
        // If data-ga4-last-muted was never set, this is the first interaction.
        // If it starts muted, it shouldn't fire 'mute' just because it loaded.
        if (wasMuted === false && isMuted === true && !vid._ga4MuteInitialized) {
          vid._ga4MuteInitialized = true;
          return; 
        }
        vid._ga4MuteInitialized = true;

        fireVideoEvent(vid, isMuted ? 'mute' : 'unmute');
      }
    }, true);
  }

  /* ═══════════════════════════════════════════════════════════
     20. brand_logo_click  (NEW — AS SEEN IN / TRUSTED BY SHOPPERS)
     Trigger: Click on partner/press logos
  ════════════════════════════════════════════════════════════ */
  function bindBrandLogoClick() {
    document.addEventListener('click', function (e) {
      var logo = closest(e.target, '[data-ga4-brand-logo]');
      if (!logo) return;

      var sectionEl = logo.hasAttribute('data-ga4-brand-section')
        ? logo
        : closest(logo, '[data-ga4-brand-section]');

      push('brand_logo_click', {
        section_name: sectionEl ? attr(sectionEl, 'data-ga4-brand-section') : 'NA',
        brand_name: attr(logo, 'data-ga4-brand-name') || text(logo.querySelector('img') ? logo : logo)
      });
    }, true);
  }

  /* ═══════════════════════════════════════════════════════════
     21. product_image_swipe  (PDP gallery)
     AUDIT FIX: image_index must be dynamic 1-based (not hardcoded 1)
     AUDIT FIX: must NOT fire horizontal_scroll instead
  ════════════════════════════════════════════════════════════ */
  function bindProductImageSwipe() {
    // 1. Thumbnail click on D-web (vertical sidebar) and M-web (horizontal strip)
    document.addEventListener('click', function (e) {
      var thumb = closest(e.target, '[data-ga4-gallery-thumb]');
      if (!thumb) return;

      var gallery = closest(thumb, '[data-ga4-image-gallery]');
      var prodName = (gallery && gallery.getAttribute('data-ga4-gallery-product')) ||
        (window.PerforaGA4 && window.PerforaGA4.product && window.PerforaGA4.product.item_name) ||
        text(document.querySelector('h1.product__title, .product-title, h1')) ||
        'NA';

      var index = parseInt(attr(thumb, 'data-ga4-gallery-thumb'), 10);
      if (isNaN(index) || index < 1) index = 1;

      push('product_image_swipe', {
        product_name: prodName,
        image_index: index
      });
    }, true);

    // 2. Mobile swipe: the theme updates .js-carousel-counter-current text when the user swipes.
    //    Watch for text changes on that span and use its value as image_index.
    var counterObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        var counter = mutation.target;
        if (!counter || !counter.classList.contains('js-carousel-counter-current')) return;

        // Only fire for product page galleries, not every carousel on the site
        var carousel = closest(counter, '[data-ga4-image-gallery]');
        if (!carousel) return;

        var index = parseInt(text(counter), 10);
        if (isNaN(index) || index < 1) return;

        var prodName = carousel.getAttribute('data-ga4-gallery-product') ||
          (window.PerforaGA4 && window.PerforaGA4.product && window.PerforaGA4.product.item_name) ||
          text(document.querySelector('h1.product__title, .product-title, h1')) ||
          'NA';

        push('product_image_swipe', {
          product_name: prodName,
          image_index: index
        });
      });
    });

    // Observe counter spans inside product image galleries
    function observeGalleryCounters() {
      var galleries = document.querySelectorAll('[data-ga4-image-gallery]');
      Array.prototype.forEach.call(galleries, function (gallery) {
        var counters = gallery.querySelectorAll('.js-carousel-counter-current');
        Array.prototype.forEach.call(counters, function (counter) {
          if (counter._ga4CounterObserved) return;
          counter._ga4CounterObserved = true;
          counterObserver.observe(counter, { childList: true, characterData: true, subtree: true });
        });
      });
    }

    ready(observeGalleryCounters);

    // Re-observe if galleries are dynamically added
    new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.hasAttribute && node.hasAttribute('data-ga4-image-gallery')) {
            var counters = node.querySelectorAll('.js-carousel-counter-current');
            Array.prototype.forEach.call(counters, function (counter) {
              if (counter._ga4CounterObserved) return;
              counter._ga4CounterObserved = true;
              counterObserver.observe(counter, { childList: true, characterData: true, subtree: true });
            });
          }
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ═══════════════════════════════════════════════════════════
     22. horizontal_scroll  (Homepage carousels & tabs on m-web)
     Triggers: Explore Oral Care, Oral Care Essentials, Shop by Concern
     Schema:
       event: 'horizontal_scroll'
       page_type: 'home_page' | 'collection_page' | 'others' etc.
       platform: 'm-web'
       section_name: 'Explore Oral Care' | 'Oral Care Essentials' | 'Shop by Concern'
       scroll_direction: 'right' | 'left'
       total_categories: string (e.g. '6', '5', '4')
       user_id_event: string | undefined
       user_type: 'logged_in' | 'guest'
  ════════════════════════════════════════════════════════════ */
  function bindHorizontalScroll() {
    var lastFired = {};

    function fireHScroll(secName, direction, totalCount) {
      if (platform() !== 'm-web') return;
      if (pageType() === 'product_page') return; // Do not fire on PDP

      var now = Date.now();
      // Debounce per section to avoid rapid duplicate events from touch+scroll
      if (lastFired[secName] && (now - lastFired[secName]) < 600) return;
      lastFired[secName] = now;

      push('horizontal_scroll', {
        section_name: secName,
        scroll_direction: direction,
        total_categories: String(totalCount || 'NA')
      });
    }

    function resolveSection(el) {
      if (!el || el.nodeType !== 1) return 'NA';

      // 1. Explore Oral Care
      if (closest(el, '.image-with-text-pro, [data-ga4-category-section="Explore Oral Care"], [data-ga4-hscroll-name="Explore Oral Care"]')) {
        return 'Explore Oral Care';
      }

      // 2. Shop by Concern
      if (closest(el, '.shop-concern-section, .shop-concern-wrapper, .concernSwiper, .shop-concern-mobile, [data-ga4-tab-section*="Concern"], [data-ga4-hscroll-name="Shop by Concern"]')) {
        return 'Shop by Concern';
      }

      // 3. Oral Care Essentials
      if (closest(el, '.tab-section-wrap, .featured-collections, [data-ga4-tab-section*="Oral Care Essentials"], [data-ga4-list-name*="Oral Care Essentials"], [data-ga4-hscroll-name="Oral Care Essentials"]')) {
        return 'Oral Care Essentials';
      }

      // 4. Other explicitly labeled containers
      var ctnr = closest(el, '[data-ga4-category-section], [data-ga4-hscroll-name], [data-ga4-list-name]');
      if (ctnr) {
        var raw = ctnr.getAttribute('data-ga4-category-section') ||
                  ctnr.getAttribute('data-ga4-hscroll-name') ||
                  ctnr.getAttribute('data-ga4-list-name') || '';
        raw = raw.replace(/<[^>]*>/g, '').trim();
        if (raw.indexOf('Oral Care Essentials') !== -1) return 'Oral Care Essentials';
        if (raw.indexOf('Shop by Concern') !== -1 || raw.indexOf('Shop By Concern') !== -1) return 'Shop by Concern';
        if (raw.indexOf('Explore Oral Care') !== -1) return 'Explore Oral Care';
        if (raw) return raw;
      }

      return 'NA';
    }

    function getCategoriesCount(container, secName) {
      if (secName === 'Explore Oral Care') {
        var items = document.querySelectorAll('.image-with-text-pro ecx-carousel-itm, .image-with-text-pro .js-carousel-item, .image-with-text-pro .clm');
        if (items.length) return items.length;
      }
      if (secName === 'Shop by Concern') {
        var tabs = document.querySelectorAll('.shop-concern-section .collection-tab, .shop-concern-mobile .collection-tab, .shop-concern-select option');
        if (tabs.length) return tabs.length;
      }
      if (secName === 'Oral Care Essentials') {
        var cTabs = document.querySelectorAll('.tab-section-wrap .tab__item, .featured-collections .tab__item');
        if (cTabs.length) return cTabs.length;
      }

      if (container && container.querySelectorAll) {
        var inC = container.querySelectorAll('[data-ga4-hscroll-item], .js-carousel-item, ecx-carousel-itm, .carousel-item, .tab__item, .collection-tab');
        if (inC.length) return inC.length;
        var attrTot = container.getAttribute('data-ga4-hscroll-total');
        if (attrTot) return attrTot;
      }
      return 'NA';
    }

    // 1. Native scroll listener (for overflow-x: auto containers e.g. Explore Oral Care, Tab headers)
    var scrollStates = {};
    var scrollTimers = {};

    document.addEventListener('scroll', function (e) {
      var el = e.target;
      if (!el || el.nodeType !== 1) return;
      if (el === document.documentElement || el === document.body) return;
      if (el.scrollWidth <= el.clientWidth) return;

      var secName = resolveSection(el);
      if (secName === 'NA') return;

      if (scrollStates[secName] === undefined) {
        scrollStates[secName] = el.scrollLeft;
      }

      clearTimeout(scrollTimers[secName]);
      scrollTimers[secName] = setTimeout(function () {
        var newLeft = el.scrollLeft;
        if (newLeft === scrollStates[secName]) return;
        var dir = newLeft > scrollStates[secName] ? 'right' : 'left';
        scrollStates[secName] = newLeft;
        var total = getCategoriesCount(el, secName);
        fireHScroll(secName, dir, total);
      }, 100);
    }, true);

    // 2. Touch gesture listener (for touch/swipe carousels like Swiper Shop by Concern & mobile carousels)
    var touchStartX = 0;
    var touchStartY = 0;
    var touchStartTime = 0;
    var touchTarget = null;

    document.addEventListener('touchstart', function (e) {
      if (!e.touches || e.touches.length !== 1) return;
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      touchStartTime = Date.now();
      touchTarget = e.target;
    }, { passive: true, capture: true });

    document.addEventListener('touchend', function (e) {
      if (!touchTarget || !e.changedTouches || e.changedTouches.length !== 1) return;
      var touchEndX = e.changedTouches[0].clientX;
      var touchEndY = e.changedTouches[0].clientY;
      var diffX = touchEndX - touchStartX;
      var diffY = touchEndY - touchStartY;
      var duration = Date.now() - touchStartTime;

      // Detect horizontal swipe gesture: > 30px horizontal, diffX > diffY, within 800ms
      if (Math.abs(diffX) >= 30 && Math.abs(diffX) > Math.abs(diffY) && duration < 800) {
        var secName = resolveSection(touchTarget);
        if (secName !== 'NA') {
          var dir = diffX < 0 ? 'right' : 'left';
          var total = getCategoriesCount(touchTarget, secName);
          fireHScroll(secName, dir, total);
        }
      }
      touchTarget = null;
    }, { passive: true, capture: true });

    // 3. Direct Swiper event hook for Shop by Concern
    function hookSwiper() {
      if (window.concernSwiper && !window.concernSwiper._ga4HScrollHooked) {
        window.concernSwiper._ga4HScrollHooked = true;
        window.concernSwiper.on('slideChange', function () {
          var sw = window.concernSwiper;
          var prev = sw._ga4PrevIndex || 0;
          var dir = sw.activeIndex >= prev ? 'right' : 'left';
          sw._ga4PrevIndex = sw.activeIndex;
          var total = getCategoriesCount(document.querySelector('.shop-concern-section'), 'Shop by Concern');
          fireHScroll('Shop by Concern', dir, total);
        });
      }
    }
    ready(hookSwiper);
    setTimeout(hookSwiper, 500);
    setTimeout(hookSwiper, 1500);
  }

  /* ── Init ────────────────────────────────────────────────── */
  ready(function () {
    // bindLogoClick(); // Handled in theme.liquid
    bindNavigation();
    bindHamburger();
    bindCategorySelection();
    bindCategoryTab();
    bindCtaClick();
    bindFooter();
    bindEmailSignup();
    bindSocialMedia();
    bindFaq();
    bindDropdown();
    bindPackSize();
    bindClinicalReports();
    bindFilterSort();
    bindCollectionCategory();
    bindSearch();
    bindScrollDepth();
    bindPincode();
    bindVideoInteraction();
    bindBrandLogoClick();
    bindProductImageSwipe();
    bindHorizontalScroll();
  });

})();
